import {
  getAllCapteurByProjectId,
  getDataFromSensors,
  updateDataFromSensors,
  deleteDataFromSensors,
  getSensorDataHistory,
  restoreDataFromSensor,
} from '../../../services/api';
import {
  parseDatetimeLocalToUnix,
  normalizeHistoryResponse,
  buildHistoryMap,
  normalizeRowsPayload,
  buildSensorsPositionsMap,
} from './useSensorDataTab.utils';

const mapSensorRows = (rows) =>
  rows.map((row, index) => ({
    ...row,
    id: row.pk_test ?? index,
    _dirty: false,
  }));

export const loadCapteurs = async (projectsList) => {
  const res = await getAllCapteurByProjectId({ projects_id: projectsList });
  const list = res?.sensors_list ?? [];
  const capteurs = Array.isArray(list) ? list : [];
  return {
    capteurs,
    initialSensorPositionMap: {},
  };
};

export const getFallbackCapteurs = () => ({
  capteurs: [],
  initialSensorPositionMap: {},
});

export const fetchSensorDataBundle = async ({
  sensorPositionMap,
  startDateTime,
  endDateTime,
  nValues,
  userId,
}) => {
  const pairs = Object.entries(sensorPositionMap).flatMap(([sId, positions]) =>
    positions.map((pos) => ({ sensorId: sId, position: pos }))
  );

  if (!pairs.length) {
    return {
      rows: [],
      historyMap: {},
      hasActiveFilters: false,
      emptySelection: true,
    };
  }

  const startTs = parseDatetimeLocalToUnix(startDateTime);
  const endTs = parseDatetimeLocalToUnix(endDateTime);
  const effectiveEndTs =
    startTs !== null
      ? (endTs ?? Math.floor(Date.now() / 1000))
      : (endTs ?? null);

  if (startTs !== null && effectiveEndTs !== null && startTs > effectiveEndTs) {
    throw new Error('La date de début doit être antérieure à la date de fin.');
  }

  const timePeriod =
    effectiveEndTs !== null
      ? [startTs !== null ? startTs : 0, effectiveEndTs]
      : undefined;

  const normalizedNValues = String(nValues ?? '').trim();
  if (normalizedNValues !== '' && !/^[1-9]\d*$/.test(normalizedNValues)) {
    throw new Error('Le champ N derniers points doit etre un entier positif.');
  }

  const nValuesParsed =
    normalizedNValues === '' ? null : Number(normalizedNValues);
  const hasActiveFilters = Boolean(timePeriod || nValuesParsed !== null);

  const sensorsPositions = buildSensorsPositionsMap(pairs);
  const bulkGetPayload = {
    sensors_positions: sensorsPositions,
    time_period: timePeriod ?? null,
    n_values: nValuesParsed,
  };

  const res = await getDataFromSensors(bulkGetPayload);
  const raw = res?.data ?? res?.rows ?? res ?? [];
  const normalizedRows = normalizeRowsPayload(raw);

  const mergedRows = mapSensorRows(
    Array.isArray(normalizedRows) ? normalizedRows : []
  )
    .map((r) => {
      const sensorId = Number(
        r.sensor_id ?? r.sensorId ?? r.id_sensor ?? r._sensorId
      );
      if (!Number.isFinite(sensorId)) return null;
      return {
        ...r,
        id: `${sensorId}_${r.id}`,
        _sensorId: sensorId,
      };
    })
    .filter(Boolean);

  const rows = mergedRows;

  const sensorIds = [
    ...new Set(
      pairs
        .map(({ sensorId }) => Number(sensorId))
        .filter((id) => Number.isFinite(id) && id > 0)
    ),
  ];
  const numericUserId = Number(userId);

  let historyMap = {};
  if (sensorIds.length > 0 && Number.isFinite(numericUserId)) {
    try {
      const res = await getSensorDataHistory({
        sensor_ids: sensorIds,
        user_id: numericUserId,
      });
      const historyList = normalizeHistoryResponse(res);
      historyMap = buildHistoryMap(historyList);
    } catch {
      historyMap = {};
    }
  }

  // Reconstruct deleted rows from history (they won't be returned by the data API)
  const existingIds = new Set(
    mergedRows.map((r) => `${r._sensorId}_${r.pk_test}`)
  );
  const deletedRows = Object.entries(historyMap)
    .filter(([, entries]) => {
      const last = entries[entries.length - 1];
      return last?.update_type === 'Delete';
    })
    .map(([, entries]) => {
      const last = entries[entries.length - 1];
      const sensorId = Number(last.sensor_id);
      const pkTest = Number(last.pk_test);
      if (!Number.isFinite(sensorId) || !Number.isFinite(pkTest)) return null;
      if (existingIds.has(`${sensorId}_${pkTest}`)) return null;
      return {
        id: `${sensorId}_${pkTest}_deleted`,
        _sensorId: sensorId,
        pk_test: pkTest,
        position_in_sensor: last.position_in_sensor ?? null,
        time_unix: last.new_time_unix ?? last.old_time_unix ?? null,
        old_time_unix: last.old_time_unix ?? null,
        value: last.new_value ?? last.old_value ?? null,
        old_value: last.old_value ?? null,
        calc_value: last.new_calc_value ?? last.old_calc_value ?? null,
        old_calc_value: last.old_calc_value ?? null,
        _dirty: false,
      };
    })
    .filter(Boolean);

  const allRows = [...rows, ...deletedRows];
  const finalRows = allRows;

  return {
    rows: finalRows,
    historyMap,
    hasActiveFilters,
    emptySelection: false,
  };
};

export const restoreSensorRow = async ({ row, userId }) => {
  const sensorId = Number(row?._sensorId);
  const pkTest = Number(row?.pk_test);
  if (!Number.isFinite(sensorId) || !Number.isFinite(pkTest)) {
    return { skipped: true, reason: 'invalid-identifiers' };
  }

  const entry = { pk_test: pkTest };
  const numericUserId = Number(userId);

  const restoredTimeUnix =
    row?.old_time_unix ?? row?.time_unix ?? row?.new_time_unix;
  const restoredValue = row?.old_value ?? row?.value ?? row?.new_value;
  const restoredCalcValue =
    row?.old_calc_value ?? row?.calc_value ?? row?.new_calc_value;
  entry.old_value = null;
  entry.old_calc_value = null;
  entry.old_time_unix = null;
  entry.position_in_sensor = row.position_in_sensor;
  if (restoredTimeUnix !== undefined && restoredTimeUnix !== null) {
    entry.time_unix = restoredTimeUnix;
  }

  if (restoredValue !== undefined && restoredValue !== null) {
    entry.value = restoredValue;
  }

  if (restoredCalcValue !== undefined && restoredCalcValue !== null) {
    entry.calc_value = restoredCalcValue;
  }

  if (
    entry.time_unix === undefined &&
    entry.value === undefined &&
    entry.calc_value === undefined
  ) {
    return { skipped: true, reason: 'no-restorable-fields' };
  }

  await restoreDataFromSensor({
    ...(Number.isFinite(numericUserId) ? { user_id: numericUserId } : {}),
    sensors_data: {
      [sensorId]: [entry],
    },
  });

  return { skipped: false };
};

export const deleteSensorRowsBulk = async ({
  targetRows,
  getNumericSensorId,
  userId,
}) => {
  const rowsToDelete = targetRows.filter(Boolean);
  if (!rowsToDelete.length) return { deletedIds: [], failedCount: 0 };

  const sensorsPkTestsMap = {};
  const validRowIds = [];
  let failedCount = 0;

  for (const row of rowsToDelete) {
    const sensorId = getNumericSensorId(row);
    if (sensorId === null) {
      failedCount += 1;
      continue;
    }
    if (!Array.isArray(sensorsPkTestsMap[sensorId])) {
      sensorsPkTestsMap[sensorId] = [];
    }
    sensorsPkTestsMap[sensorId].push(row.pk_test);
    validRowIds.push(row.id);
  }

  if (!validRowIds.length) {
    return { deletedIds: [], failedCount };
  }

  const bulkDeletePayload = {
    ...(Number.isFinite(Number(userId)) ? { user_id: Number(userId) } : {}),
    sensors_pk_tests: sensorsPkTestsMap,
  };
  const deletedIds = new Set();

  try {
    await deleteDataFromSensors(bulkDeletePayload);
    validRowIds.forEach((id) => deletedIds.add(id));
  } catch {
    failedCount += validRowIds.length;
  }

  return { deletedIds: Array.from(deletedIds), failedCount };
};

export const saveSensorEditsBulk = async ({
  edits,
  getNumericSensorId,
  userId,
}) => {
  const sensorsDataMap = {};
  const validEditIds = [];
  let failedCount = 0;

  for (const row of edits) {
    const sensorId = getNumericSensorId(row);
    if (sensorId === null) {
      failedCount += 1;
      continue;
    }

    if (!Array.isArray(sensorsDataMap[sensorId])) {
      sensorsDataMap[sensorId] = [];
    }

    const oldTimeUnix = row.old_time_unix ?? row.time_unix;
    const oldValue = row.old_value ?? row.value;
    const oldCalcValue = row.old_calc_value ?? row.calc_value;

    const entry = { pk_test: row.pk_test };

    if (row.time_unix !== oldTimeUnix) {
      entry.time_unix = row.time_unix;
      entry.old_time_unix = oldTimeUnix;
    }
    if (row.value !== oldValue) {
      entry.value = row.value;
      entry.old_value = oldValue;
    }
    if (row.calc_value !== oldCalcValue) {
      entry.calc_value = row.calc_value;
      entry.old_calc_value = oldCalcValue;
    }

    if (
      entry.time_unix === undefined &&
      entry.value === undefined &&
      entry.calc_value === undefined
    ) {
      continue;
    }

    sensorsDataMap[sensorId].push(entry);
    validEditIds.push(row.id);
  }

  if (!validEditIds.length) {
    return { savedIds: [], failedCount };
  }

  const bulkUpdatePayload = {
    ...(Number.isFinite(Number(userId)) ? { user_id: Number(userId) } : {}),
    sensors_data: sensorsDataMap,
  };
  const savedIds = new Set();

  try {
    await updateDataFromSensors(bulkUpdatePayload);
    validEditIds.forEach((id) => savedIds.add(id));
  } catch {
    failedCount += validEditIds.length;
  }

  return { savedIds: Array.from(savedIds), failedCount };
};
